import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PaneResizeHandle } from '@/components/ui/pane-resize-handle'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { useResizablePane } from '@/hooks/use-resizable-pane'
import { useAuth } from '@/lib/auth'
import {
  type SalesUser,
  type SalesUserImportSummary,
  inspectSalesUsersCsvContent,
  isSalesContributorRole,
} from '@/lib/business-workflow'
import {
  type SalesUserFileImportPreviewClientResult,
  importSalesUsersCsvToApi,
  previewSalesUserFileImportToApi,
  saveSalesUserToApi,
  syncSalesUsersFromWebhookToApi,
} from '@/lib/data-client/customers'
import { isApiDataClientMode } from '@/lib/data-client/mode'
import { useBusinessWorkflowDataClientHydration } from '@/lib/data-client/use-business-workflow-hydration'
import { hasFormAdminAccess } from '@/lib/permissions'
import { getTwoPaneGridTemplate, salesDialogPaneDefaults } from '@/lib/resizable-panes'
import { useSystemFeatureVisibility } from '@/lib/system-feature-visibility'
import { cn } from '@/lib/utils'
import { useBusinessWorkflowStore } from '@/stores/businessWorkflowStore'
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS as DndCss } from '@dnd-kit/utilities'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowUpDown,
  CircleDashed,
  Download,
  FileSpreadsheet,
  FileUp,
  GripVertical,
  KeyRound,
  LockKeyhole,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  type UserCog,
  Users,
  Workflow,
} from 'lucide-react'
import { type CSSProperties, useEffect, useId, useMemo, useState } from 'react'

const salesStatusMeta: Record<SalesUser['status'], { label: string; className: string }> = {
  active: {
    label: '啟用',
    className: 'border-emerald-200 bg-emerald-100 text-emerald-800',
  },
  inactive: {
    label: '停用',
    className: 'border-slate-200 bg-slate-100 text-slate-700',
  },
  blocked: {
    label: '封鎖',
    className: 'border-rose-200 bg-rose-100 text-rose-800',
  },
}

const salesRoleMeta: Record<
  SalesUser['role'],
  { label: string; variant: 'default' | 'secondary' | 'outline' }
> = {
  manager: { label: '業務主管', variant: 'default' },
  sales: { label: '業務', variant: 'secondary' },
  assistant: { label: '業務助理', variant: 'outline' },
}

const salesUsersCsvTemplate = [
  '業務簡稱,業務代號,業務email,部門別,部門代號,組別代號,直屬主管,狀態',
  '王小明,S001,sales@example.test,苗實 104,104,104,林主管,在職',
  '林主管,M001,manager@example.test,苗實 104,104,104,,在職',
].join('\n')

const salesUsersSupervisorCsvTemplate = ['業務簡稱,直屬主管', '王小明,林主管'].join('\n')

type SalesListSort =
  | 'custom'
  | 'name_asc'
  | 'name_desc'
  | 'department_asc'
  | 'department_desc'
  | 'role_asc'
  | 'status_asc'

const salesListCustomOrderStorageKey = 'miaoform:sales-list-custom-order:v1'

const salesListSortOptions: Array<{ value: SalesListSort; label: string }> = [
  { value: 'custom', label: '自訂排序' },
  { value: 'name_asc', label: '姓名 A-Z' },
  { value: 'name_desc', label: '姓名 Z-A' },
  { value: 'department_asc', label: '部門 A-Z' },
  { value: 'department_desc', label: '部門 Z-A' },
  { value: 'role_asc', label: '角色' },
  { value: 'status_asc', label: '狀態' },
]

const salesFileCorrectionCsvTemplate = [
  '業務簡稱,業務代號,業務email,部門別,部門代號,組別代號,直屬主管,主管email,狀態',
  '王小明,S001,sales@example.test,苗實 104,104,104,林主管,manager@example.test,active',
].join('\n')

const salesUsersCsvTemplateHref = `data:text/csv;charset=utf-8,${encodeURIComponent(
  salesUsersCsvTemplate,
)}`

const salesUsersSupervisorCsvTemplateHref = `data:text/csv;charset=utf-8,${encodeURIComponent(
  salesUsersSupervisorCsvTemplate,
)}`

const salesFileCorrectionCsvTemplateHref = `data:text/csv;charset=utf-8,${encodeURIComponent(
  salesFileCorrectionCsvTemplate,
)}`

const fileImportContentTypes = new Set(['application/pdf', 'image/png', 'image/jpeg', 'image/webp'])

function inferSalesUserFileImportFormat(file: File) {
  const extension = file.name.split('.').pop()?.toLocaleLowerCase() ?? ''
  if (extension === 'pdf') return 'pdf'
  if (extension === 'png') return 'png'
  if (extension === 'jpg') return 'jpg'
  if (extension === 'jpeg') return 'jpeg'
  if (extension === 'webp') return 'webp'
  if (file.type === 'application/pdf') return 'pdf'
  if (file.type === 'image/png') return 'png'
  if (file.type === 'image/jpeg') return 'jpg'
  if (file.type === 'image/webp') return 'webp'
  return ''
}

function formatFileSize(sizeBytes: number) {
  if (sizeBytes < 1024) return `${sizeBytes} bytes`
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`
  return `${(sizeBytes / 1024 / 1024).toFixed(1)} MB`
}

function escapeCsvCell(value: string) {
  const text = value.replace(/\r?\n/g, ' ').trim()
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

function buildMissingSupervisorEmailCsv(salesUsers: SalesUser[]) {
  const rows = [
    ['業務簡稱', '直屬主管', '主管email'],
    ...salesUsers.map((salesUser) => [salesUser.name, salesUser.supervisorName, '']),
  ]

  return rows.map((row) => row.map(escapeCsvCell).join(',')).join('\n')
}

function readSalesUserCsvFile(file: File): Promise<string> {
  if (typeof file.text === 'function') {
    return file.text()
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(new Error('無法讀取 CSV 檔案。'))
    reader.readAsText(file, 'utf-8')
  })
}

function loadSalesListCustomOrder() {
  if (typeof window === 'undefined') return []
  try {
    const parsed = JSON.parse(window.localStorage.getItem(salesListCustomOrderStorageKey) ?? '[]')
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === 'string')
      : []
  } catch {
    return []
  }
}

function saveSalesListCustomOrder(order: string[]) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(salesListCustomOrderStorageKey, JSON.stringify(order))
}

function sortSalesUsers(users: SalesUser[], sort: SalesListSort, customOrder: string[]) {
  if (sort === 'custom') {
    const orderIndex = new Map(customOrder.map((id, index) => [id, index]))
    return [...users].sort((left, right) => {
      const leftIndex = orderIndex.get(left.id) ?? Number.MAX_SAFE_INTEGER
      const rightIndex = orderIndex.get(right.id) ?? Number.MAX_SAFE_INTEGER
      if (leftIndex !== rightIndex) return leftIndex - rightIndex
      return compareSalesText(left.name, right.name)
    })
  }

  return [...users].sort((left, right) => {
    if (sort === 'name_desc') return compareSalesText(right.name, left.name)
    if (sort === 'department_asc') {
      return (
        compareSalesText(left.departmentName, right.departmentName) ||
        compareSalesText(left.departmentCode, right.departmentCode) ||
        compareSalesText(left.name, right.name)
      )
    }
    if (sort === 'department_desc') {
      return (
        compareSalesText(right.departmentName, left.departmentName) ||
        compareSalesText(right.departmentCode, left.departmentCode) ||
        compareSalesText(left.name, right.name)
      )
    }
    if (sort === 'role_asc') {
      return compareSalesText(salesRoleMeta[left.role].label, salesRoleMeta[right.role].label)
    }
    if (sort === 'status_asc') {
      return (
        compareSalesText(salesStatusMeta[left.status].label, salesStatusMeta[right.status].label) ||
        compareSalesText(left.name, right.name)
      )
    }
    return compareSalesText(left.name, right.name)
  })
}

function compareSalesText(left: string | null | undefined, right: string | null | undefined) {
  return String(left ?? '').localeCompare(String(right ?? ''), 'zh-Hant', {
    numeric: true,
    sensitivity: 'base',
  })
}

function getSalesUserRowClassName(status: SalesUser['status']) {
  if (status === 'active') return 'bg-emerald-50/60 hover:bg-emerald-50'
  if (status === 'blocked') return 'bg-rose-50/70 hover:bg-rose-50'
  return 'bg-slate-50 hover:bg-slate-100'
}

export default function SalesRoute() {
  const { user, isLoading } = useAuth()
  const queryClient = useQueryClient()
  useBusinessWorkflowDataClientHydration(!isLoading)
  const salesUsers = useBusinessWorkflowStore((state) => state.salesUsers)
  const upsertSalesUser = useBusinessWorkflowStore((state) => state.upsertSalesUser)
  const importSalesUsersCsv = useBusinessWorkflowStore((state) => state.importSalesUsersCsv)
  const canManageSales = hasFormAdminAccess(user)
  const featureVisibility = useSystemFeatureVisibility()
  const canUseSalesUserImport = featureVisibility['sales.importCsv']
  const canUseSalesUserFileImport = featureVisibility['sales.fileImport']
  const [keyword, setKeyword] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState('all')
  const [salesSort, setSalesSort] = useState<SalesListSort>('custom')
  const [customSalesOrder, setCustomSalesOrder] = useState<string[]>(() =>
    loadSalesListCustomOrder(),
  )
  const [roleFilter, setRoleFilter] = useState<'all' | SalesUser['role']>('all')
  const [statusFilter, setStatusFilter] = useState<'all' | SalesUser['status']>('all')
  const [editingUser, setEditingUser] = useState<SalesUser | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [fileImportDialogOpen, setFileImportDialogOpen] = useState(false)
  const [csvContent, setCsvContent] = useState('')
  const [supervisorCsvContent, setSupervisorCsvContent] = useState('')
  const [importSummary, setImportSummary] = useState<SalesUserImportSummary | null>(null)
  const [importError, setImportError] = useState<string | null>(null)
  const [isImporting, setIsImporting] = useState(false)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)
  const [syncError, setSyncError] = useState<string | null>(null)
  const [isSyncing, setIsSyncing] = useState(false)
  const [statusUpdateError, setStatusUpdateError] = useState<string | null>(null)
  const [statusUpdatingIds, setStatusUpdatingIds] = useState<Set<string>>(() => new Set())
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const departments = useMemo(
    () =>
      Array.from(
        new Map(
          salesUsers.map((item) => [
            item.departmentCode || item.departmentName || 'uncategorized',
            item.departmentName || '未設定部門',
          ]),
        ).entries(),
      ),
    [salesUsers],
  )
  const missingSupervisorEmailUsers = useMemo(
    () =>
      salesUsers.filter(
        (item) => item.supervisorName.trim().length > 0 && item.supervisorEmail.trim().length === 0,
      ),
    [salesUsers],
  )
  const missingSupervisorEmailCount = missingSupervisorEmailUsers.length

  const filtered = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLocaleLowerCase()
    return salesUsers.filter((salesUser) => {
      const departmentKey = salesUser.departmentCode || salesUser.departmentName || 'uncategorized'
      const matchesDepartment = departmentFilter === 'all' || departmentKey === departmentFilter
      const matchesRole = roleFilter === 'all' || salesUser.role === roleFilter
      const matchesStatus = statusFilter === 'all' || salesUser.status === statusFilter
      const matchesKeyword =
        !normalizedKeyword ||
        [
          salesUser.name,
          salesUser.email,
          salesUser.title,
          salesUser.departmentName,
          salesUser.departmentCode,
          salesUser.teamCode,
          salesUser.supervisorName,
          salesUser.territory,
          salesUser.customerSegment,
        ]
          .join(' ')
          .toLocaleLowerCase()
          .includes(normalizedKeyword)
      return matchesDepartment && matchesRole && matchesStatus && matchesKeyword
    })
  }, [departmentFilter, keyword, roleFilter, salesUsers, statusFilter])

  const sortedSalesUsers = useMemo(
    () => sortSalesUsers(filtered, salesSort, customSalesOrder),
    [customSalesOrder, filtered, salesSort],
  )

  if (isLoading) {
    return <div className="grid min-h-dvh place-items-center text-sm text-slate-500">載入中...</div>
  }

  if (!canManageSales) {
    return (
      <div className="grid min-h-dvh place-items-center bg-slate-50 p-6 text-center">
        <div>
          <h1 className="text-lg font-semibold text-slate-950">沒有業務管理權限</h1>
          <p className="mt-2 text-sm text-slate-500">只有管理員可以調整業務名單與主管關係。</p>
        </div>
      </div>
    )
  }

  async function saveEditingUser() {
    if (!editingUser) return
    setSaveError(null)
    const supervisor = salesUsers.find((item) => item.email === editingUser.supervisorEmail)
    const nextUser = {
      ...editingUser,
      password: editingUser.password?.trim() || undefined,
      removePassword: editingUser.removePassword ? true : undefined,
      supervisorName: supervisor?.name ?? editingUser.supervisorName,
      supervisorEmail:
        editingUser.role === 'manager' && !editingUser.supervisorEmail
          ? editingUser.email
          : editingUser.supervisorEmail,
    }
    try {
      setIsSaving(true)
      const saved = await saveSalesUserToApi(nextUser)
      upsertSalesUser(saved)
      await queryClient.invalidateQueries({ queryKey: ['business-workflow-data-client'] })
      setEditingUser(null)
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : '儲存業務失敗。')
    } finally {
      setIsSaving(false)
    }
  }

  function openCreateUser() {
    setSaveError(null)
    setEditingUser(createDraftSalesUser())
  }

  async function applySalesImportCsv() {
    setImportError(null)
    setImportSummary(null)
    if (!csvContent.trim()) {
      setImportError('請先貼上 CSV 名單。')
      return
    }

    try {
      setIsImporting(true)
      if (isApiDataClientMode()) {
        const result = await importSalesUsersCsvToApi(csvContent, supervisorCsvContent)
        for (const salesUser of result.salesUsers) upsertSalesUser(salesUser)
        setImportSummary(result.summary)
        await queryClient.invalidateQueries({ queryKey: ['business-workflow-data-client'] })
        return
      }

      setImportSummary(importSalesUsersCsv(csvContent, supervisorCsvContent))
    } catch (error) {
      setImportError(error instanceof Error ? error.message : '匯入業務名單失敗。')
    } finally {
      setIsImporting(false)
    }
  }

  async function syncSalesUsers() {
    setSyncMessage(null)
    setSyncError(null)
    if (!isApiDataClientMode()) {
      setSyncMessage('同步業務需要 API 模式；目前本機 demo 不會呼叫外部 webhook。')
      return
    }

    try {
      setIsSyncing(true)
      const result = await syncSalesUsersFromWebhookToApi()
      await queryClient.invalidateQueries({ queryKey: ['business-workflow-data-client'] })
      setSyncMessage(
        `${result.message} 已讀取 ${result.pagesFetched} 頁、接收 ${result.usersReceived} 筆，更新業務 ${result.salesUsersUpserted} 筆，更新管理員 ${result.adminsUpserted} 筆，略過受保護帳戶 ${result.skippedProtected} 筆。`,
      )
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : '同步業務 webhook 失敗。')
    } finally {
      setIsSyncing(false)
    }
  }

  async function updateSalesUserStatus(salesUser: SalesUser, status: SalesUser['status']) {
    if (salesUser.status === status) return
    setStatusUpdateError(null)
    setStatusUpdatingIds((current) => new Set(current).add(salesUser.id))
    try {
      const saved = await saveSalesUserToApi({ ...salesUser, status })
      upsertSalesUser(saved)
      await queryClient.invalidateQueries({ queryKey: ['business-workflow-data-client'] })
    } catch (error) {
      console.error('[MiaoForm] sales user status update failed', {
        salesUserId: salesUser.id,
        status,
        error,
      })
      setStatusUpdateError('更新業務狀態失敗，請稍後再試。')
    } finally {
      setStatusUpdatingIds((current) => {
        const next = new Set(current)
        next.delete(salesUser.id)
        return next
      })
    }
  }

  function downloadMissingSupervisorEmailCsv() {
    if (missingSupervisorEmailUsers.length === 0) return
    const csv = buildMissingSupervisorEmailCsv(missingSupervisorEmailUsers)
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'miaoform-missing-supervisor-email.csv'
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  function handleSalesDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const visibleIds = sortedSalesUsers.map((salesUser) => salesUser.id)
    const oldIndex = visibleIds.indexOf(String(active.id))
    const newIndex = visibleIds.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    const nextVisibleOrder = arrayMove(visibleIds, oldIndex, newIndex)
    const nextOrder = [
      ...nextVisibleOrder,
      ...customSalesOrder.filter((id) => !visibleIds.includes(id)),
    ]
    setCustomSalesOrder(nextOrder)
    saveSalesListCustomOrder(nextOrder)
  }

  return (
    <div className="flex flex-col gap-5 p-4 lg:p-6">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">業務管理</h1>
          <p className="mt-1 text-sm text-slate-500">
            以 Email 判斷登入者是哪位業務、業務助理或主管，並維護部門與主管資料。
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canUseSalesUserImport ? (
            <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
              <FileSpreadsheet className="size-4" />
              匯入業務
            </Button>
          ) : null}
          {canUseSalesUserFileImport ? (
            <Button variant="outline" onClick={() => setFileImportDialogOpen(true)}>
              <FileUp className="size-4" />
              上傳圖片/PDF
            </Button>
          ) : null}
          <Button variant="outline" onClick={syncSalesUsers} disabled={isSyncing}>
            <RefreshCw className={cn('size-4', isSyncing && 'animate-spin')} />
            {isSyncing ? '同步中...' : '同步業務'}
          </Button>
          <Button onClick={openCreateUser}>
            <Plus className="size-4" />
            新增業務
          </Button>
        </div>
      </div>

      {syncMessage ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {syncMessage}
        </div>
      ) : null}
      {syncError ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {syncError}
        </div>
      ) : null}
      {statusUpdateError ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {statusUpdateError}
        </div>
      ) : null}
      {missingSupervisorEmailCount > 0 ? (
        <output className="flex flex-col gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
            <div>
              <div className="font-medium">
                有 {missingSupervisorEmailCount} 筆業務缺少主管 Email
              </div>
              <div className="mt-1 text-amber-800">
                主管姓名已保留；請用主管對照 CSV 補齊 email，讓登入與審核權限判斷更完整。
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={downloadMissingSupervisorEmailCsv}>
              <Download className="size-4" />
              下載補件 CSV
            </Button>
            {canUseSalesUserImport ? (
              <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
                <FileSpreadsheet className="size-4" />
                匯入主管對照
              </Button>
            ) : null}
          </div>
        </output>
      ) : null}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          icon={Users}
          label="業務成員"
          value={`${salesUsers.length}`}
          detail="含主管、業務與業務助理"
        />
        <MetricCard
          icon={ShieldCheck}
          label="主管"
          value={`${salesUsers.filter((item) => item.role === 'manager').length}`}
          detail="依主管設定處理審核"
        />
        <MetricCard
          icon={Users}
          label="業務助理"
          value={`${salesUsers.filter((item) => item.role === 'assistant').length}`}
          detail="目前沿用業務權限"
        />
        <MetricCard
          icon={Workflow}
          label="部門"
          value={`${departments.length}`}
          detail="依部門與主管資料管理清單"
        />
      </section>

      <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-3 lg:grid-cols-[minmax(260px,1fr)_220px_180px_180px_180px]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={keyword}
            onChange={(event) => setKeyword(event.currentTarget.value)}
            placeholder="搜尋姓名、Email、部門、主管、區域"
            className="pl-9"
          />
        </div>
        <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部部門</SelectItem>
            {departments.map(([departmentKey, departmentName]) => (
              <SelectItem key={departmentKey} value={departmentKey}>
                {departmentName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={roleFilter}
          onValueChange={(value) => setRoleFilter(value as typeof roleFilter)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部角色</SelectItem>
            {Object.entries(salesRoleMeta).map(([value, meta]) => (
              <SelectItem key={value} value={value}>
                {meta.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as typeof statusFilter)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全部狀態</SelectItem>
            {Object.entries(salesStatusMeta).map(([value, meta]) => (
              <SelectItem key={value} value={value}>
                {meta.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={salesSort} onValueChange={(value) => setSalesSort(value as SalesListSort)}>
          <SelectTrigger>
            <ArrowUpDown className="size-4 text-slate-400" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {salesListSortOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">排序</TableHead>
                <TableHead>姓名</TableHead>
                <TableHead>業務代號</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>角色</TableHead>
                <TableHead>部門</TableHead>
                <TableHead>主管</TableHead>
                <TableHead>狀態</TableHead>
                <TableHead>操作</TableHead>
              </TableRow>
            </TableHeader>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={salesSort === 'custom' ? handleSalesDragEnd : undefined}
            >
              <SortableContext
                items={sortedSalesUsers.map((salesUser) => salesUser.id)}
                strategy={verticalListSortingStrategy}
              >
                <TableBody>
                  {sortedSalesUsers.map((salesUser) => (
                    <SortableSalesUserRow
                      key={salesUser.id}
                      salesUser={salesUser}
                      canDrag={salesSort === 'custom'}
                      onEdit={() => {
                        setSaveError(null)
                        setEditingUser({ ...salesUser, password: '', removePassword: false })
                      }}
                      isStatusUpdating={statusUpdatingIds.has(salesUser.id)}
                      onStatusChange={(status) => updateSalesUserStatus(salesUser, status)}
                    />
                  ))}
                </TableBody>
              </SortableContext>
            </DndContext>
          </Table>
        </div>
      </div>

      {canUseSalesUserImport ? (
        <SalesImportDialog
          open={importDialogOpen}
          onOpenChange={setImportDialogOpen}
          csvContent={csvContent}
          onCsvContentChange={setCsvContent}
          supervisorCsvContent={supervisorCsvContent}
          onSupervisorCsvContentChange={setSupervisorCsvContent}
          summary={importSummary}
          error={importError}
          isImporting={isImporting}
          onImport={applySalesImportCsv}
        />
      ) : null}

      {canUseSalesUserFileImport ? (
        <SalesFileImportDialog
          open={fileImportDialogOpen}
          onOpenChange={setFileImportDialogOpen}
          canSwitchToCsvImport={canUseSalesUserImport}
          onSwitchToCsvImport={() => {
            setFileImportDialogOpen(false)
            setImportDialogOpen(true)
          }}
        />
      ) : null}

      <SalesUserDialog
        user={editingUser}
        salesUsers={salesUsers}
        onChange={(patch) =>
          setEditingUser((current) => (current ? { ...current, ...patch } : current))
        }
        onOpenChange={(open) => {
          if (!open) {
            setEditingUser(null)
            setSaveError(null)
          }
        }}
        onSave={saveEditingUser}
        isSaving={isSaving}
        error={saveError}
      />
    </div>
  )
}

function SortableSalesUserRow({
  salesUser,
  canDrag,
  onEdit,
  isStatusUpdating,
  onStatusChange,
}: {
  salesUser: SalesUser
  canDrag: boolean
  onEdit: () => void
  isStatusUpdating: boolean
  onStatusChange: (status: SalesUser['status']) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: salesUser.id,
    disabled: !canDrag,
  })
  const style: CSSProperties = {
    transform: DndCss.Transform.toString(transform),
    transition,
  }

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={cn(
        'border-b border-slate-200 transition-colors',
        getSalesUserRowClassName(salesUser.status),
        isDragging && 'relative z-10',
      )}
    >
      <TableCell>
        <button
          type="button"
          className={cn(
            'grid size-8 place-items-center rounded-md border border-slate-200 bg-white text-slate-500',
            canDrag
              ? 'cursor-grab hover:bg-slate-50 active:cursor-grabbing'
              : 'cursor-default opacity-40',
          )}
          aria-label="拖移調整排序"
          disabled={!canDrag}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" />
        </button>
      </TableCell>
      <TableCell className="font-medium text-slate-950">{salesUser.name}</TableCell>
      <TableCell>
        <span className="font-mono text-sm text-slate-700">{salesUser.employeeId || '-'}</span>
      </TableCell>
      <TableCell>{salesUser.email}</TableCell>
      <TableCell>
        <Badge variant={salesRoleMeta[salesUser.role].variant}>
          {salesRoleMeta[salesUser.role].label}
        </Badge>
      </TableCell>
      <TableCell>
        <div>{salesUser.departmentName || '未設定部門'}</div>
        {salesUser.departmentCode || salesUser.teamCode ? (
          <div className="mt-1 text-xs text-slate-500">
            {[salesUser.departmentCode, salesUser.teamCode ? `組別 ${salesUser.teamCode}` : '']
              .filter(Boolean)
              .join(' / ')}
          </div>
        ) : null}
      </TableCell>
      <TableCell>{salesUser.supervisorName}</TableCell>
      <TableCell>
        <Select
          value={salesUser.status}
          onValueChange={(value) => onStatusChange(value as SalesUser['status'])}
          disabled={isStatusUpdating}
        >
          <SelectTrigger
            className={cn(
              'h-9 w-[116px] bg-white text-sm',
              salesStatusMeta[salesUser.status].className,
            )}
            aria-label={`調整 ${salesUser.name} 狀態`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(salesStatusMeta).map(([value, meta]) => (
              <SelectItem key={value} value={value}>
                {isStatusUpdating && value === salesUser.status ? '更新中...' : meta.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <Button variant="outline" size="sm" onClick={onEdit}>
          編輯
        </Button>
      </TableCell>
    </tr>
  )
}

function createDraftSalesUser(): SalesUser {
  return {
    id: '',
    email: '',
    name: '',
    employeeId: '',
    title: '',
    phone: '',
    teamId: 'formal-sales-team',
    teamName: '正式業務團隊',
    teamCode: '',
    departmentName: '',
    departmentCode: '',
    role: 'sales',
    supervisorEmail: '',
    supervisorName: '',
    status: 'active',
    territory: '',
    customerSegment: '',
    notes: '',
    password: '',
    removePassword: false,
    passwordConfigured: false,
  }
}

function SalesImportDialog({
  open,
  onOpenChange,
  csvContent,
  onCsvContentChange,
  supervisorCsvContent,
  onSupervisorCsvContentChange,
  summary,
  error,
  isImporting,
  onImport,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  csvContent: string
  onCsvContentChange: (value: string) => void
  supervisorCsvContent: string
  onSupervisorCsvContentChange: (value: string) => void
  summary: SalesUserImportSummary | null
  error: string | null
  isImporting: boolean
  onImport: () => void
}) {
  const csvInputId = useId()
  const csvFileInputId = useId()
  const supervisorCsvInputId = useId()
  const supervisorCsvFileInputId = useId()
  const [csvFileName, setCsvFileName] = useState('')
  const [supervisorCsvFileName, setSupervisorCsvFileName] = useState('')
  const [csvFileError, setCsvFileError] = useState<string | null>(null)
  const csvHeaderInspection = useMemo(() => inspectSalesUsersCsvContent(csvContent), [csvContent])
  const csvBlockingErrors = [
    ...(csvHeaderInspection?.missingRequired.map((field) => `缺少必要欄位 ${field}。`) ?? []),
    ...(csvHeaderInspection?.rowErrors ?? []),
  ]
  const hasBlockingCsvHeaderError = csvBlockingErrors.length > 0

  async function loadCsvFile(file: File | undefined) {
    setCsvFileError(null)
    if (!file) return
    const isCsvLike =
      file.type === 'text/csv' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.toLocaleLowerCase().endsWith('.csv')
    if (!isCsvLike) {
      setCsvFileName('')
      setCsvFileError('目前只支援 CSV 檔案；Excel 仍需 dependency review。')
      return
    }

    try {
      const text = await readSalesUserCsvFile(file)
      onCsvContentChange(text)
      setCsvFileName(file.name)
    } catch {
      setCsvFileName('')
      setCsvFileError('讀取 CSV 檔案失敗，請改用貼上內容。')
    }
  }

  async function loadSupervisorCsvFile(file: File | undefined) {
    setCsvFileError(null)
    if (!file) return
    const isCsvLike =
      file.type === 'text/csv' ||
      file.type === 'application/vnd.ms-excel' ||
      file.name.toLocaleLowerCase().endsWith('.csv')
    if (!isCsvLike) {
      setSupervisorCsvFileName('')
      setCsvFileError('直屬主管對照目前只支援 CSV 檔案。')
      return
    }

    try {
      const text = await readSalesUserCsvFile(file)
      onSupervisorCsvContentChange(text)
      setSupervisorCsvFileName(file.name)
    } catch {
      setSupervisorCsvFileName('')
      setCsvFileError('讀取直屬主管 CSV 失敗，請改用貼上內容。')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[86dvh] w-[min(96vw,760px)] max-w-none flex-col overflow-hidden p-0">
        <div className="border-b border-slate-200 px-6 py-5">
          <DialogHeader>
            <DialogTitle>匯入業務名單</DialogTitle>
            <DialogDescription>
              CSV 會以 Email 比對新增或更新業務；不會自動刪除、封鎖或停用既有業務。
            </DialogDescription>
          </DialogHeader>
        </div>
        <ScrollArea className="min-h-0 flex-1" viewportClassName="space-y-5 px-6 py-5">
          <div className="rounded-md border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
            CSV 新增/更新：以 Email
            比對，新增不存在的業務，更新既有業務的姓名、部門、團隊與直屬主管。
          </div>
          <div className="rounded-md border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
            API/D1 模式會走後端 safe
            upsert：可以更新既有正式業務基本資料，但仍會保留既有狀態與密碼，不接受匯入檔把人員改成封鎖。
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Excel 需 dependency review；目前不啟用 xlsx parser。
          </div>
          <div className="grid gap-3 rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-700">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="font-medium text-slate-950">CSV 欄位格式</div>
              <div className="flex flex-wrap gap-2">
                <a
                  aria-label="下載 CSV 範本"
                  className="inline-flex h-9 items-center justify-center rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  href={salesUsersCsvTemplateHref}
                  download="miaoform-sales-users-template.csv"
                >
                  下載主名單範本
                </a>
                <a
                  className="inline-flex h-9 items-center justify-center rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  href={salesUsersSupervisorCsvTemplateHref}
                  download="miaoform-sales-supervisors-template.csv"
                >
                  下載主管對照範本
                </a>
              </div>
            </div>
            <div className="grid gap-2 text-xs leading-6 text-slate-600">
              <div>主名單必要欄位：email 或 業務email。</div>
              <div>
                中文欄位可用：業務簡稱、業務代號、業務email、部門別、部門代號、組別代號、直屬主管、狀態。
              </div>
              <div>主管對照可用：業務簡稱、直屬主管；會用名稱匹配主名單中的 email。</div>
              <div>
                建議欄位：name, role, status, teamId, teamName, supervisorEmail, title,
                departmentName, departmentCode, teamCode, phone
              </div>
              <div>角色可用值：manager / sales / assistant，也可用業務主管 / 業務 / 業務助理。</div>
              <div>狀態可用值：active / inactive / blocked，也可用啟用 / 停用 / 封鎖。</div>
            </div>
          </div>
          <div className="grid gap-2 rounded-md border border-slate-200 bg-white p-4">
            <Label htmlFor={csvFileInputId}>選擇 CSV 檔案</Label>
            <Input
              id={csvFileInputId}
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => void loadCsvFile(event.currentTarget.files?.[0])}
            />
            {csvFileName ? (
              <div className="text-xs text-emerald-700">已載入 {csvFileName}</div>
            ) : null}
            {csvFileError ? <div className="text-xs text-rose-600">{csvFileError}</div> : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor={csvInputId}>主名單 CSV</Label>
            <Textarea
              aria-label="CSV 名單"
              id={csvInputId}
              value={csvContent}
              onChange={(event) => onCsvContentChange(event.currentTarget.value)}
              className="min-h-56 font-mono text-xs"
              placeholder="業務簡稱,業務代號,業務email,部門別,部門代號,組別代號,直屬主管,狀態"
            />
            {csvHeaderInspection ? (
              <div
                className={cn(
                  'rounded-md border px-3 py-2 text-xs',
                  hasBlockingCsvHeaderError
                    ? 'border-rose-200 bg-rose-50 text-rose-700'
                    : 'border-emerald-200 bg-emerald-50 text-emerald-700',
                )}
              >
                {hasBlockingCsvHeaderError ? (
                  <div className="grid gap-1">
                    {csvBlockingErrors.map((csvError) => (
                      <div key={csvError}>{csvError}</div>
                    ))}
                  </div>
                ) : (
                  `已辨識 ${csvHeaderInspection.headerCount} 個欄位，可套用匯入。`
                )}
              </div>
            ) : null}
          </div>
          <div className="grid gap-2 rounded-md border border-slate-200 bg-white p-4">
            <Label htmlFor={supervisorCsvFileInputId}>選擇直屬主管對照 CSV</Label>
            <Input
              id={supervisorCsvFileInputId}
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => void loadSupervisorCsvFile(event.currentTarget.files?.[0])}
            />
            {supervisorCsvFileName ? (
              <div className="text-xs text-emerald-700">已載入 {supervisorCsvFileName}</div>
            ) : null}
            <div className="text-xs text-slate-500">
              選填；若有提供，系統會用「業務簡稱」對應「直屬主管」並設定主管 email。
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor={supervisorCsvInputId}>直屬主管對照 CSV</Label>
            <Textarea
              id={supervisorCsvInputId}
              value={supervisorCsvContent}
              onChange={(event) => onSupervisorCsvContentChange(event.currentTarget.value)}
              className="min-h-36 font-mono text-xs"
              placeholder="業務簡稱,直屬主管"
            />
          </div>
          {error ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          ) : null}
          {summary ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <ImportSummaryTile label="已讀取" value={`已讀取 ${summary.imported} 筆`} />
              <ImportSummaryTile label="新增" value={`新增 ${summary.created} 筆`} />
              <ImportSummaryTile
                label="略過既有"
                value={`略過既有 ${summary.skippedExisting} 筆`}
              />
              {summary.updated && summary.updated > 0 ? (
                <ImportSummaryTile label="更新" value={`更新 ${summary.updated} 筆`} />
              ) : null}
              {summary.statusChangesIgnored && summary.statusChangesIgnored > 0 ? (
                <ImportSummaryTile
                  label="略過狀態異動"
                  value={`略過狀態異動 ${summary.statusChangesIgnored} 筆`}
                />
              ) : null}
            </div>
          ) : null}
          {summary?.errors.length ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {summary.errors.join('；')}
            </div>
          ) : null}
        </ScrollArea>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button onClick={onImport} disabled={isImporting || hasBlockingCsvHeaderError}>
            {isImporting ? '匯入中...' : '套用匯入名單'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ImportSummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-semibold text-slate-950">{value}</div>
    </div>
  )
}

function SalesFileImportActivationChecklist({ providerState }: { providerState: string }) {
  const providerConfigured =
    providerState !== 'provider_not_configured' && providerState !== 'adapter_not_enabled'
  const items = [
    {
      label: 'R2 暫存上傳檔已設定',
      detail: 'PDF/圖片原始檔必須先安全暫存，才進入解析流程。',
      status: providerConfigured ? '待驗證' : '待設定',
      Icon: providerConfigured ? CircleDashed : AlertTriangle,
      className: providerConfigured
        ? 'border-slate-200 bg-slate-50 text-slate-700'
        : 'border-amber-200 bg-amber-50 text-amber-800',
    },
    {
      label: 'OCR/AI provider 已確認',
      detail: '解析供應商未啟用前，只能顯示匯入狀態，不能產生待寫入資料。',
      status: providerConfigured ? '待驗證' : '待設定',
      Icon: providerConfigured ? CircleDashed : AlertTriangle,
      className: providerConfigured
        ? 'border-slate-200 bg-slate-50 text-slate-700'
        : 'border-amber-200 bg-amber-50 text-amber-800',
    },
    {
      label: '人工校正欄位已確認',
      detail: '需確認業務簡稱、Email、部門、主管與狀態欄位後，才允許送出。',
      status: '待確認',
      Icon: CircleDashed,
      className: 'border-slate-200 bg-slate-50 text-slate-700',
    },
    {
      label: '管理員確認後才寫入 D1',
      detail: '前端不會因為檔案上傳成功就自動新增、封鎖或刪除業務。',
      status: '已鎖定',
      Icon: LockKeyhole,
      className: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    },
  ]

  return (
    <div className="rounded-md border border-slate-200 bg-white p-4">
      <div className="flex flex-col gap-1">
        <h3 className="font-semibold text-slate-950">啟用條件</h3>
        <p className="text-xs text-slate-500">
          圖片/PDF 匯入要同時通過以下條件，才會開放人工確認與 D1 寫入。
        </p>
      </div>
      <div className="mt-4 grid gap-2">
        {items.map(({ label, detail, status, Icon, className }) => (
          <div
            key={label}
            className={cn('flex items-start gap-3 rounded-md border px-3 py-2', className)}
          >
            <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div className="font-medium">{label}</div>
                <Badge variant="outline" className="w-fit bg-white/70 text-[11px]">
                  {status}
                </Badge>
              </div>
              <div className="mt-1 text-xs opacity-80">{detail}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function SalesFileImportDialog({
  open,
  onOpenChange,
  canSwitchToCsvImport,
  onSwitchToCsvImport,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  canSwitchToCsvImport: boolean
  onSwitchToCsvImport: () => void
}) {
  const fileInputId = useId()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewResult, setPreviewResult] = useState<SalesUserFileImportPreviewClientResult | null>(
    null,
  )
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [isChecking, setIsChecking] = useState(false)

  async function checkFileImportProviderGate() {
    setPreviewError(null)
    setPreviewResult(null)
    if (!selectedFile) {
      setPreviewError('請先選擇圖片或 PDF 檔案。')
      return
    }

    const format = inferSalesUserFileImportFormat(selectedFile)
    if (!format || !fileImportContentTypes.has(selectedFile.type)) {
      setPreviewError('目前只接受 PDF、PNG、JPG、JPEG 或 WEBP。')
      return
    }

    try {
      setIsChecking(true)
      const result = await previewSalesUserFileImportToApi({
        format,
        fileName: selectedFile.name,
        contentType: selectedFile.type,
        sizeBytes: selectedFile.size,
        languageHint: 'zh-TW',
      })
      setPreviewResult(result)
    } catch (error) {
      setPreviewError(error instanceof Error ? error.message : '檢查檔案匯入狀態失敗。')
    } finally {
      setIsChecking(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(94vw,640px)] max-w-none">
        <DialogHeader>
          <DialogTitle>上傳圖片/PDF 匯入業務名單</DialogTitle>
          <DialogDescription>
            PDF 或圖片會先解析成預覽資料，人工確認後才可以新增或更新業務資料。
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm text-slate-600">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
            目前後端已保留 `/api/sales-users/import/files/preview` provider-gated route；在 R2
            暫存、Workers AI 或 OCR 解析、人工確認畫面核准前，不會把圖片/PDF 解析結果直接寫入 D1。
          </div>
          <div className="flex flex-wrap gap-2">
            <a
              href={salesFileCorrectionCsvTemplateHref}
              download="miaoform-sales-file-correction-template.csv"
              className="inline-flex w-fit items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-900 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <Download className="size-4" aria-hidden="true" />
              下載人工校正 CSV 範本
            </a>
            {canSwitchToCsvImport ? (
              <Button variant="outline" onClick={onSwitchToCsvImport}>
                <FileSpreadsheet className="size-4" aria-hidden="true" />
                改用 CSV 匯入
              </Button>
            ) : null}
          </div>
          <div className="grid gap-3 rounded-md border border-slate-200 bg-white p-4">
            <Label htmlFor={fileInputId}>圖片/PDF 檔案</Label>
            <Input
              id={fileInputId}
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              onChange={(event) => {
                const file = event.currentTarget.files?.[0] ?? null
                setSelectedFile(file)
                setPreviewResult(null)
                setPreviewError(null)
              }}
            />
            {selectedFile ? (
              <div className="text-xs text-slate-500">
                已選擇 {selectedFile.name}（{selectedFile.type || 'unknown'}，
                {formatFileSize(selectedFile.size)}）
              </div>
            ) : null}
            <div>
              <Button onClick={checkFileImportProviderGate} disabled={isChecking}>
                {isChecking ? '檢查中...' : '檢查檔案匯入狀態'}
              </Button>
            </div>
            {previewError ? (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-rose-700">
                {previewError}
              </div>
            ) : null}
            {previewResult ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-800">
                {previewResult.code === 'provider_not_configured'
                  ? '檔案解析供應商尚未啟用，沒有寫入 D1。'
                  : previewResult.message}
                <div className="mt-1 text-xs">
                  狀態：{previewResult.providerState ?? previewResult.code}；raw payload：未保存。
                </div>
              </div>
            ) : null}
          </div>
          {previewResult ? (
            <SalesFileImportActivationChecklist
              providerState={previewResult.providerState ?? previewResult.code}
            />
          ) : null}
          {previewResult ? (
            <div className="rounded-md border border-slate-200 bg-white p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h3 className="font-semibold text-slate-950">人工校正預覽</h3>
                  <p className="mt-1 text-xs text-slate-500">
                    必要欄位：業務簡稱、業務email、部門別、部門代號、組別代號、直屬主管、狀態。
                  </p>
                </div>
                <Badge
                  variant="outline"
                  className="w-fit border-amber-200 bg-amber-50 text-amber-800"
                >
                  待解析
                </Badge>
              </div>
              <div className="mt-4 overflow-hidden rounded-md border border-slate-200">
                <div className="grid grid-cols-[1.1fr_1.5fr_1fr_0.8fr_0.8fr_1fr_0.8fr] bg-slate-50 text-xs font-medium text-slate-500">
                  {[
                    '業務簡稱',
                    '業務email',
                    '部門別',
                    '部門代號',
                    '組別代號',
                    '直屬主管',
                    '狀態',
                  ].map((column) => (
                    <div
                      key={column}
                      className="border-r border-slate-200 px-3 py-2 last:border-r-0"
                    >
                      {column}
                    </div>
                  ))}
                </div>
                <div className="px-3 py-6 text-center text-sm text-slate-500">
                  解析尚未啟用，沒有可校正的預覽資料。
                </div>
              </div>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-slate-500">
                  Provider 啟用後，這裡會顯示 OCR/AI 解析結果；管理員校正後才會寫入 D1。
                </div>
                <Button disabled>確認寫入 D1</Button>
              </div>
            </div>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-3">
            <ImportSummaryTile label="支援格式規劃" value="PDF / 圖片" />
            <ImportSummaryTile label="寫入方式" value="人工確認後" />
            <ImportSummaryTile label="刪除/封鎖" value="不自動執行" />
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-amber-800">
            CSV 匯入已可直接新增或更新 D1。PDF/圖片匯入仍需啟用檔案保存、OCR/AI
            解析與人工確認流程，啟用前只會檢查供應商狀態。
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function SalesUserDialog({
  user,
  salesUsers,
  onChange,
  onOpenChange,
  onSave,
  isSaving,
  error,
}: {
  user: SalesUser | null
  salesUsers: SalesUser[]
  onChange: (patch: Partial<SalesUser>) => void
  onOpenChange: (open: boolean) => void
  onSave: () => void
  isSaving: boolean
  error: string | null
}) {
  const [activePanel, setActivePanel] = useState<'basic' | 'settings' | 'password' | 'erp'>('basic')
  const {
    width: navWidth,
    collapsed: navCollapsed,
    startResize: startNavResize,
    toggleCollapsed: toggleNavCollapsed,
  } = useResizablePane({
    initialWidth: 220,
    config: salesDialogPaneDefaults.nav,
  })
  const salesDialogGridColumns = getTwoPaneGridTemplate({
    navWidth,
    navCollapsed,
    contentMinWidth: 0,
    navConfig: salesDialogPaneDefaults.nav,
  })
  const userId = user?.id

  useEffect(() => {
    if (userId) setActivePanel('basic')
  }, [userId])

  return (
    <Dialog open={Boolean(user)} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[min(86dvh,760px)] max-h-none w-[min(96vw,980px)] max-w-none flex-col overflow-hidden p-0">
        <div className="shrink-0 border-b border-slate-200 px-6 py-5">
          <DialogHeader>
            <DialogTitle>業務資料與主管設定</DialogTitle>
            <DialogDescription className="sr-only">
              編輯業務基本資訊、團隊與主管設定。
            </DialogDescription>
          </DialogHeader>
        </div>
        {user ? (
          <>
            <div
              data-testid="sales-user-dialog-grid"
              className="grid min-h-0 flex-1 transition-[grid-template-columns] duration-200 ease-out lg:grid-cols-[var(--sales-user-grid-columns)]"
              style={{ '--sales-user-grid-columns': salesDialogGridColumns } as CSSProperties}
            >
              <div className="relative min-h-0 min-w-0 overflow-hidden">
                <aside
                  className={cn(
                    'min-h-0 border-b border-slate-200 bg-slate-50 transition-opacity duration-150 lg:h-full lg:border-r lg:border-b-0',
                    navCollapsed && 'pointer-events-none opacity-0',
                  )}
                  aria-hidden={navCollapsed}
                >
                  <div className="h-full overflow-y-auto overscroll-contain p-3 [scrollbar-gutter:stable]">
                    <nav className="grid gap-1" aria-label="業務資料區塊">
                      {[
                        { id: 'basic', label: '帳戶資訊' },
                        { id: 'password', label: '密碼設定' },
                        { id: 'settings', label: '設定' },
                        { id: 'erp', label: 'ERP資料' },
                      ].map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setActivePanel(item.id as typeof activePanel)}
                          className={cn(
                            'h-10 rounded-md px-3 text-left text-sm font-medium text-slate-600 hover:bg-white',
                            activePanel === item.id && 'bg-white text-indigo-700 shadow-sm',
                          )}
                        >
                          {item.label}
                        </button>
                      ))}
                    </nav>
                  </div>
                </aside>
                <PaneResizeHandle
                  edge="right"
                  collapsed={navCollapsed}
                  resizeLabel="拖曳調整業務設定左欄寬度"
                  collapseLabel="收合業務設定左欄"
                  expandLabel="展開業務設定左欄"
                  onResizeStart={startNavResize}
                  onToggle={toggleNavCollapsed}
                />
              </div>
              <div
                key={activePanel}
                className="min-h-0 overflow-y-auto overscroll-contain px-6 py-5 [scrollbar-gutter:stable]"
              >
                {activePanel === 'basic' ? (
                  <div className="grid gap-5 lg:grid-cols-2">
                    <Field
                      label="Email"
                      value={user.email}
                      onChange={(email) => onChange({ email })}
                    />
                    <Field label="姓名" value={user.name} onChange={(name) => onChange({ name })} />
                    <Field
                      label="業務代號"
                      value={user.employeeId}
                      onChange={(employeeId) => onChange({ employeeId })}
                    />
                    <Field
                      label="職稱"
                      value={user.title}
                      onChange={(title) => onChange({ title })}
                    />
                    <Field
                      label="負責區域"
                      value={user.territory}
                      onChange={(territory) => onChange({ territory })}
                    />
                    <Field
                      label="部門別"
                      value={user.departmentName}
                      onChange={(departmentName) => onChange({ departmentName })}
                    />
                    <Field
                      label="部門代號"
                      value={user.departmentCode}
                      onChange={(departmentCode) => onChange({ departmentCode })}
                    />
                    <Field
                      label="組別代號"
                      value={user.teamCode}
                      onChange={(teamCode) => onChange({ teamCode })}
                    />
                    <div className="lg:col-span-2">
                      <Label>備註</Label>
                      <Textarea
                        className="mt-2"
                        value={user.notes}
                        onChange={(event) => onChange({ notes: event.currentTarget.value })}
                      />
                    </div>
                  </div>
                ) : null}
                {activePanel === 'settings' ? (
                  <div className="grid gap-5 lg:grid-cols-2">
                    <SelectField
                      label="角色"
                      value={user.role}
                      options={[
                        { value: 'sales', label: '業務' },
                        { value: 'assistant', label: '業務助理' },
                        { value: 'manager', label: '業務主管' },
                      ]}
                      onChange={(role) =>
                        onChange({
                          role: role as SalesUser['role'],
                          ...(role === 'manager'
                            ? { supervisorEmail: '', supervisorName: '' }
                            : {}),
                        })
                      }
                    />
                    <SelectField
                      label="狀態"
                      value={user.status}
                      options={[
                        { value: 'active', label: '啟用' },
                        { value: 'inactive', label: '停用' },
                        { value: 'blocked', label: '封鎖' },
                      ]}
                      onChange={(status) => onChange({ status: status as SalesUser['status'] })}
                    />
                    <Field
                      label="團隊"
                      value={user.teamName}
                      onChange={(teamName) => onChange({ teamName })}
                    />
                    {isSalesContributorRole(user.role) ? (
                      <SelectField
                        label="所屬主管"
                        value={user.supervisorEmail}
                        options={salesUsers
                          .filter((item) => item.role === 'manager')
                          .map((item) => ({ value: item.email, label: item.name }))}
                        onChange={(supervisorEmail) => onChange({ supervisorEmail })}
                      />
                    ) : null}
                  </div>
                ) : null}
                {activePanel === 'password' ? (
                  <SalesPasswordPanel
                    passwordConfigured={user.passwordConfigured}
                    password={user.password}
                    removePassword={user.removePassword}
                    onChange={onChange}
                  />
                ) : null}
                {activePanel === 'erp' ? <SalesUserErpDataPanel user={user} /> : null}
              </div>
            </div>
            <div className="flex shrink-0 justify-end gap-2 border-t border-slate-200 px-6 py-4">
              {error ? (
                <div className="mr-auto self-center text-sm text-rose-600">{error}</div>
              ) : null}
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                取消
              </Button>
              <Button onClick={onSave} disabled={isSaving}>
                {isSaving ? '儲存中...' : '儲存業務'}
              </Button>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

function SalesPasswordPanel({
  passwordConfigured,
  password,
  removePassword,
  onChange,
}: {
  passwordConfigured?: boolean
  password?: string
  removePassword?: boolean
  onChange: (patch: Partial<SalesUser>) => void
}) {
  const passwordId = useId()
  const hasPendingPassword = Boolean(password?.trim())
  const statusLabel = removePassword
    ? '將移除密碼'
    : hasPendingPassword || passwordConfigured
      ? '已設定密碼'
      : '未設定密碼'
  const statusClass = removePassword
    ? 'border-amber-200 bg-amber-50 text-amber-700'
    : hasPendingPassword || passwordConfigured
      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
      : 'border-slate-200 bg-slate-100 text-slate-600'

  return (
    <div className="space-y-5 rounded-md border border-slate-200 bg-slate-50/60 p-4">
      <div className="flex items-center gap-2">
        <div className="grid size-8 place-items-center rounded-md bg-white text-indigo-700 shadow-sm">
          <KeyRound className="size-4" />
        </div>
        <h2 className="font-semibold">密碼設定</h2>
      </div>
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="w-full max-w-md space-y-2">
          <Label htmlFor={passwordId}>新密碼</Label>
          <Input
            id={passwordId}
            type="password"
            autoComplete="new-password"
            value={password ?? ''}
            onChange={(event) => {
              const nextPassword = event.currentTarget.value
              onChange({
                password: nextPassword,
                removePassword: false,
              })
            }}
          />
        </div>
        <Badge variant="outline" className={cn('w-fit', statusClass)}>
          {statusLabel}
        </Badge>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={!passwordConfigured && !hasPendingPassword}
          onClick={() =>
            onChange({
              password: '',
              removePassword: true,
              passwordConfigured: false,
            })
          }
        >
          移除密碼
        </Button>
        {removePassword ? (
          <Button type="button" variant="ghost" onClick={() => onChange({ removePassword: false })}>
            取消移除
          </Button>
        ) : null}
      </div>
    </div>
  )
}

function SalesUserErpDataPanel({ user }: { user: SalesUser }) {
  const erpData = user.erpData
  if (!erpData) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-600">
        尚無同步 ERP 資料。只有管理員點擊「同步業務」且 webhook
        回傳該人員資料後，這裡才會顯示唯讀資料。
      </div>
    )
  }

  const hasUnmatchedDirectManager =
    Boolean(erpData.directManager?.trim()) &&
    !user.supervisorEmail.trim() &&
    !user.supervisorName.trim()
  const rows = [
    ['同步時間', erpData.syncedAt],
    ['業務代號', erpData.employeeId],
    ['Email', erpData.email],
    ['姓名', erpData.name],
    ['角色', getSalesRoleLabel(erpData.role)],
    ['狀態', erpData.status ? getSalesStatusLabel(erpData.status) : '未提供'],
    ['職稱', erpData.title],
    ['電話', erpData.phone],
    ['團隊', erpData.teamName],
    ['組別代號', erpData.teamCode],
    ['所在地', erpData.location],
    ['部門別', erpData.departmentName],
    ['部門代號', erpData.departmentCode],
    ['直屬主管', erpData.supervisorName],
    ['主管 Email', erpData.supervisorEmail],
    ['ERP 直屬主管', erpData.directManager],
    ['部門主管', erpData.departmentManager],
    ['副總', erpData.vicePresident],
    ['負責區域', erpData.territory],
    ['客群', erpData.customerSegment],
  ] as const

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        此區只顯示同步來源資料，不可在這裡修改。同步時只會把本機空白欄位補上 ERP
        資料，不會覆蓋已有欄位。
      </div>
      {hasUnmatchedDirectManager ? (
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          ERP 回傳的直屬主管尚未對應到業務清單中的主管帳號，正式直屬主管欄位暫不更新；可在此
          ERP資料查看來源值。
        </div>
      ) : null}
      <div className="grid gap-3 lg:grid-cols-2">
        {rows.map(([label, value]) => (
          <ReadOnlyField key={label} label={label} value={value} />
        ))}
      </div>
    </div>
  )
}

function ReadOnlyField({ label, value }: { label: string; value?: string }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="min-h-10 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
        {value?.trim() || '-'}
      </div>
    </div>
  )
}

function getSalesRoleLabel(role: SalesUser['role']) {
  if (role === 'manager') return '業務主管'
  if (role === 'assistant') return '業務助理'
  return '業務'
}

function getSalesStatusLabel(status: SalesUser['status']) {
  if (status === 'inactive') return '停用'
  if (status === 'blocked') return '封鎖'
  return '啟用'
}

function MetricCard({
  icon: Icon,
  label,
  value,
  detail,
}: {
  icon: typeof UserCog
  label: string
  value: string
  detail: string
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-5">
        <div className="grid size-11 place-items-center rounded-lg bg-indigo-50 text-indigo-700">
          <Icon className="size-5" />
        </div>
        <div>
          <div className="text-sm text-slate-500">{label}</div>
          <div className="mt-1 text-2xl font-semibold">{value}</div>
          <div className="mt-1 text-xs text-slate-500">{detail}</div>
        </div>
      </CardContent>
    </Card>
  )
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input value={value} onChange={(event) => onChange(event.currentTarget.value)} />
    </div>
  )
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
